import { useEffect, useMemo, useState } from "react";
import { API_BASE_URL, getAssetUrl, loginAdmin, savePortfolioData, uploadResume } from "../api";

const AUTH_STORAGE_KEY = "portfolio_admin_session";

const cloneData = (value) => JSON.parse(JSON.stringify(value));
const linesToArray = (value) => value.split("\n").map((item) => item.trim()).filter(Boolean);
const arrayToLines = (value) => (value || []).join("\n");
const stackToText = (value) => (value || []).join(", ");
const textToStack = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);

const adminCss = `
  .pa-shell, .pa-shell * { box-sizing: border-box; }
  .pa-shell { min-height: 100vh; background: var(--off-white); color: var(--text-main); }
  .pa-animate { animation: paFade 0.32s ease both; }
  @keyframes paFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  .pa-login { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 2rem; background: var(--off-white); }
  .pa-login-card { width: min(400px, 100%); }
  .pa-sidebar { width: 240px; min-height: 100vh; background: var(--white); border-right: 1px solid var(--border); display: flex; flex-direction: column; position: fixed; left: 0; top: 0; bottom: 0; z-index: 50; }
  .pa-main { margin-left: 240px; flex: 1; max-width: calc(100vw - 240px); overflow-x: hidden; padding: 2.5rem; }
  .pa-card { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 1.5rem; }
  .pa-input, .pa-textarea { background: var(--white); border: 1px solid var(--border); border-radius: 8px; padding: 0.65rem 0.9rem; font: inherit; font-size: 0.875rem; color: var(--text-main); width: 100%; outline: none; transition: border-color 0.2s; }
  .pa-input:focus, .pa-textarea:focus { border-color: var(--text-main); }
  .pa-textarea { resize: vertical; }
  .pa-label { display: block; margin-bottom: 4px; color: var(--text-muted); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.05em; text-transform: uppercase; }
  .pa-primary, .pa-outline, .pa-danger { border-radius: 999px; padding: 0.6rem 1.4rem; font: inherit; font-size: 0.85rem; cursor: pointer; transition: opacity 0.2s, background 0.2s; }
  .pa-primary { background: var(--text-main); color: var(--white); border: 0; font-weight: 500; }
  .pa-primary:disabled { cursor: wait; opacity: 0.6; }
  .pa-outline { background: transparent; color: var(--text-secondary); border: 1px solid var(--border); }
  .pa-outline:disabled { cursor: default; opacity: 0.55; }
  .pa-danger { background: transparent; color: #c0392b; border: 1px solid #f0c5bf; padding: 0.45rem 1rem; font-size: 0.8rem; }
  .pa-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
  .pa-grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; }
  .pa-grid-4 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1rem; }
  .pa-skill-card { padding: 1.25rem; background: linear-gradient(180deg, #ffffff 0%, #fbfaf8 100%); }
  .pa-skill-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border); }
  .pa-skill-title { min-width: 0; }
  .pa-skill-title strong { display: block; overflow: hidden; color: var(--text-main); font-size: 1rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
  .pa-skill-title span { display: block; margin-top: 0.15rem; overflow: hidden; color: var(--text-muted); font-size: 0.78rem; text-overflow: ellipsis; white-space: nowrap; }
  .pa-icon-button { display: inline-flex; width: 32px; height: 32px; flex-shrink: 0; align-items: center; justify-content: center; border: 1px solid #f0c5bf; border-radius: 8px; background: #fff8f6; color: #c0392b; cursor: pointer; font: inherit; font-size: 1rem; line-height: 1; padding: 0 0 2px; text-align: center; }
  .pa-icon-button:hover { background: #fdf2f1; }
  .pa-skills-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1rem; align-items: start; }
  .pa-skill-fields { display: flex; flex-direction: column; gap: 0.85rem; }
  .pa-skill-slider { margin-top: 1rem; border: 1px solid var(--border); border-radius: 8px; background: var(--white); padding: 0.85rem 1rem; }
  .pa-range { width: 100%; height: 4px; accent-color: var(--text-main); cursor: pointer; }
  .pa-record-card { padding: 0; overflow: hidden; background: linear-gradient(180deg, #ffffff 0%, #fbfaf8 100%); }
  .pa-record-summary { display: flex; align-items: center; gap: 1rem; width: 100%; padding: 1rem 1.25rem; cursor: pointer; background: transparent; border: 0; text-align: left; font: inherit; transition: background 0.15s; }
  .pa-record-summary.active { background: var(--off-white); }
  .pa-record-main { min-width: 0; flex: 1; }
  .pa-record-main strong { display: block; overflow: hidden; color: var(--text-main); font-size: 0.96rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
  .pa-record-main span { display: block; margin-top: 0.15rem; overflow: hidden; color: var(--text-muted); font-size: 0.78rem; text-overflow: ellipsis; white-space: nowrap; }
  .pa-record-chip { flex-shrink: 0; max-width: 190px; overflow: hidden; border: 1px solid var(--border); border-radius: 999px; background: var(--light-gray); padding: 0.2rem 0.65rem; color: var(--text-muted); font-size: 0.72rem; text-overflow: ellipsis; white-space: nowrap; }
  .pa-record-action { flex-shrink: 0; color: var(--text-muted); font-size: 0.78rem; }
  .pa-record-body { display: flex; flex-direction: column; gap: 1rem; border-top: 1px solid var(--border); padding: 1.25rem; }
  .pa-record-body-head { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
  .pa-record-body-head strong { overflow: hidden; font-size: 0.9rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
  .pa-top-actions { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-bottom: 1.5rem; }
  .pa-status { font-size: 0.82rem; color: var(--text-muted); }
  .pa-status.success { color: #277a48; }
  .pa-status.danger { color: #c0392b; }
  .pa-section-title { font-family: var(--serif); font-size: 1.75rem; line-height: 1.15; letter-spacing: -0.02em; }
  .pa-section-sub { color: var(--text-muted); font-size: 0.85rem; margin-top: 4px; }
  .pa-mobile-bar { display: none; }
  @media (max-width: 900px) {
    .pa-sidebar { display: none; }
    .pa-main { margin-left: 0; max-width: none; padding: 1rem; }
    .pa-mobile-bar { display: flex; gap: 0.5rem; overflow-x: auto; margin-bottom: 1rem; }
    .pa-grid-2, .pa-grid-3, .pa-grid-4 { grid-template-columns: 1fr; }
    .pa-skills-grid { grid-template-columns: 1fr; }
    .pa-skill-head { align-items: stretch; flex-direction: column; }
    .pa-record-summary { align-items: flex-start; flex-direction: column; }
    .pa-record-body-head { align-items: stretch; flex-direction: column; }
    .pa-top-actions { align-items: stretch; flex-direction: column; }
  }
`;

const navItems = [
  { id: "overview", icon: "O", label: "Overview" },
  { id: "profile", icon: "P", label: "Profile" },
  { id: "skills", icon: "S", label: "Skills" },
  { id: "projects", icon: "W", label: "Projects" },
  { id: "experience", icon: "E", label: "Experience" },
  { id: "resume", icon: "R", label: "Resume" },
];

function Field({ label, children }) {
  return (
    <div>
      <label className="pa-label">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      className="pa-input"
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      className="pa-textarea"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      rows={rows}
    />
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <h2 className="pa-section-title">{title}</h2>
      {sub && <p className="pa-section-sub">{sub}</p>}
    </div>
  );
}

function Login({ onLogin }) {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus("");

    try {
      const session = await loginAdmin(credentials);
      onLogin(session);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pa-login">
      <style>{adminCss}</style>
      <form className="pa-login-card pa-animate" onSubmit={handleSubmit}>
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", letterSpacing: "-0.02em", marginBottom: "0.4rem" }}>
            Portfolio Admin
          </div>
          <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Sign in to manage your portfolio</p>
        </div>
        <div className="pa-card">
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <Field label="Username">
              <Input
                value={credentials.username}
                onChange={(value) => setCredentials((current) => ({ ...current, username: value }))}
                placeholder="admin"
              />
            </Field>
            <Field label="Password">
              <Input
                value={credentials.password}
                onChange={(value) => setCredentials((current) => ({ ...current, password: value }))}
                placeholder="Password"
                type="password"
              />
            </Field>
            {status && (
              <p style={{ fontSize: "0.82rem", color: "#c0392b", background: "#fdf2f1", border: "1px solid #f5c6c2", borderRadius: 6, padding: "0.5rem 0.75rem" }}>
                {status}
              </p>
            )}
            <button className="pa-primary" type="submit" disabled={loading} style={{ width: "100%", padding: "0.8rem", marginTop: 4 }}>
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Sidebar({ active, setActive, onLogout, name }) {
  return (
    <aside className="pa-sidebar">
      <div style={{ padding: "1.5rem 1.25rem 1rem", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", letterSpacing: "-0.02em" }}>{name || "Portfolio"}</div>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 2 }}>Portfolio Admin</div>
      </div>
      <nav style={{ flex: 1, padding: "0.75rem" }}>
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActive(item.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.65rem",
              width: "100%",
              padding: "0.6rem 0.75rem",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              background: active === item.id ? "var(--off-white)" : "transparent",
              color: active === item.id ? "var(--text-main)" : "var(--text-secondary)",
              font: "inherit",
              fontSize: "0.875rem",
              fontWeight: active === item.id ? 500 : 400,
              marginBottom: 2,
              textAlign: "left",
            }}
          >
            <span style={{ display: "grid", placeItems: "center", width: 20, height: 20, borderRadius: 999, background: "var(--light-gray)", fontSize: "0.72rem" }}>{item.icon}</span>
            {item.label}
            {active === item.id && <span style={{ marginLeft: "auto", width: 5, height: 5, borderRadius: "50%", background: "var(--text-main)" }} />}
          </button>
        ))}
      </nav>
      <div style={{ padding: "1rem 1.25rem", borderTop: "1px solid var(--border)" }}>
        <button type="button" onClick={onLogout} className="pa-outline" style={{ width: "100%", fontSize: "0.8rem", padding: "0.55rem" }}>
          Sign out
        </button>
      </div>
    </aside>
  );
}

function MobileNav({ active, setActive }) {
  return (
    <div className="pa-mobile-bar">
      {navItems.map((item) => (
        <button
          key={item.id}
          type="button"
          className={active === item.id ? "pa-primary" : "pa-outline"}
          onClick={() => setActive(item.id)}
          style={{ whiteSpace: "nowrap" }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function Overview({ draft }) {
  const counts = [
    { label: "Projects", val: draft.projects.length, icon: "Work" },
    { label: "Skills", val: draft.skills.length, icon: "Skill" },
    { label: "Experiences", val: draft.experience.length, icon: "Role" },
    { label: "Tech chips", val: draft.chips.length, icon: "Tech" },
  ];

  return (
    <div className="pa-animate">
      <SectionHeader title="Overview" sub="Summary of your portfolio content" />
      <div className="pa-grid-4" style={{ marginBottom: "2rem" }}>
        {counts.map((count) => (
          <div key={count.label} className="pa-card">
            <div style={{ fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
              {count.icon} / {count.label}
            </div>
            <div style={{ fontFamily: "var(--serif)", fontSize: "2.5rem", lineHeight: 1 }}>{count.val}</div>
          </div>
        ))}
      </div>
      <div className="pa-grid-2">
        <div className="pa-card">
          <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "1rem" }}>
            Recent Projects
          </div>
          {draft.projects.slice(0, 3).map((project, index) => (
            <div key={`${project.title}-${index}`} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.6rem 0", borderBottom: "1px solid var(--border)" }}>
              <span style={{ fontSize: "0.72rem", background: "var(--off-white)", border: "1px solid var(--border)", borderRadius: 999, padding: "0.15rem 0.6rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                {project.tag || "No tag"}
              </span>
              <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{project.title || "Untitled project"}</span>
            </div>
          ))}
        </div>
        <div className="pa-card">
          <div style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "1rem" }}>
            Experience Timeline
          </div>
          {draft.experience.map((item, index) => (
            <div key={`${item.company}-${index}`} style={{ display: "flex", gap: "0.75rem", padding: "0.6rem 0", borderBottom: "1px solid var(--border)", alignItems: "flex-start" }}>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", minWidth: 90, paddingTop: 2 }}>{item.period || "No period"}</span>
              <div>
                <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>{item.role || "Untitled role"}</div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{item.company}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddChip({ onAdd }) {
  const [value, setValue] = useState("");
  const add = () => {
    if (!value.trim()) return;
    onAdd(value.trim());
    setValue("");
  };

  return (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <input
        className="pa-input"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") add();
        }}
        placeholder="Add a technology..."
        style={{ flex: 1 }}
      />
      <button type="button" onClick={add} className="pa-outline" style={{ whiteSpace: "nowrap", padding: "0.6rem 1rem" }}>
        Add
      </button>
    </div>
  );
}

function ProfileEditor({ draft, setDraft }) {
  const setRoot = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const setContact = (key, value) => setDraft((current) => ({ ...current, contact: { ...current.contact, [key]: value } }));
  const setStat = (index, key, value) => {
    setDraft((current) => ({
      ...current,
      stats: current.stats.map((stat, itemIndex) => (itemIndex === index ? { ...stat, [key]: value } : stat)),
    }));
  };
  const setBio = (index, value) => {
    setDraft((current) => ({
      ...current,
      bio: current.bio.map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
  };

  return (
    <div className="pa-animate">
      <SectionHeader title="Profile" sub="Your hero section content and contact details" />
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        <div className="pa-card">
          <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "1rem" }}>
            Basic Info
          </div>
          <div className="pa-grid-2">
            <Field label="Full Name"><Input value={draft.name} onChange={(value) => setRoot("name", value)} placeholder="Your name" /></Field>
            <Field label="Title"><Input value={draft.title} onChange={(value) => setRoot("title", value)} placeholder="Senior Mobile Engineer" /></Field>
            <Field label="Tagline"><Input value={draft.tagline} onChange={(value) => setRoot("tagline", value)} placeholder="Available for freelance" /></Field>
          </div>
        </div>

        <div className="pa-card">
          <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "1rem" }}>
            Hero Headline
          </div>
          <div className="pa-grid-3">
            {draft.headline.map((headline, index) => (
              <Field key={index} label={`Line ${index + 1}`}>
                <Input
                  value={headline}
                  onChange={(value) => setRoot("headline", draft.headline.map((item, itemIndex) => (itemIndex === index ? value : item)))}
                />
              </Field>
            ))}
          </div>
        </div>

        <div className="pa-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", gap: "1rem" }}>
            <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Bio Paragraphs
            </div>
            <button type="button" onClick={() => setRoot("bio", [...draft.bio, ""])} className="pa-outline" style={{ fontSize: "0.78rem", padding: "0.35rem 0.9rem" }}>
              Add paragraph
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {draft.bio.map((bio, index) => (
              <div key={index} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}><Textarea value={bio} onChange={(value) => setBio(index, value)} rows={3} /></div>
                <button type="button" onClick={() => setRoot("bio", draft.bio.filter((_, itemIndex) => itemIndex !== index))} className="pa-danger" style={{ borderRadius: 8, padding: "0.5rem 0.7rem", flexShrink: 0 }}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="pa-card">
          <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "1rem" }}>
            Hero Stats
          </div>
          <div className="pa-grid-3">
            {draft.stats.map((stat, index) => (
              <div key={index} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <Field label="Number"><Input value={stat.num} onChange={(value) => setStat(index, "num", value)} placeholder="8+" /></Field>
                <Field label="Label"><Input value={stat.label} onChange={(value) => setStat(index, "label", value)} placeholder="Years of experience" /></Field>
              </div>
            ))}
          </div>
        </div>

        <div className="pa-card">
          <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "1rem" }}>
            Tech Stack Chips
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
            {draft.chips.map((chip, index) => (
              <span key={`${chip}-${index}`} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", background: "var(--off-white)", border: "1px solid var(--border)", borderRadius: 999, padding: "0.25rem 0.75rem", fontSize: "0.8rem" }}>
                {chip}
                <button type="button" onClick={() => setRoot("chips", draft.chips.filter((_, itemIndex) => itemIndex !== index))} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.75rem", lineHeight: 1, padding: 0 }}>
                  x
                </button>
              </span>
            ))}
          </div>
          <AddChip onAdd={(chip) => setRoot("chips", [...draft.chips, chip])} />
        </div>

        <div className="pa-card">
          <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "1rem" }}>
            Contact
          </div>
          <div className="pa-grid-3">
            <Field label="Email"><Input value={draft.contact.email} onChange={(value) => setContact("email", value)} placeholder="you@example.com" /></Field>
            <Field label="LinkedIn"><Input value={draft.contact.linkedin} onChange={(value) => setContact("linkedin", value)} placeholder="linkedin.com/in/you" /></Field>
            <Field label="GitHub"><Input value={draft.contact.github} onChange={(value) => setContact("github", value)} placeholder="github.com/you" /></Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function SkillsEditor({ skills, setDraft }) {
  const update = (index, key, value) => {
    setDraft((current) => ({
      ...current,
      skills: current.skills.map((skill, itemIndex) => (itemIndex === index ? { ...skill, [key]: value } : skill)),
    }));
  };

  return (
    <div className="pa-animate">
      <SectionHeader title="Skills" sub="Manage your skill bars and proficiency levels" />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
        <button type="button" onClick={() => setDraft((current) => ({ ...current, skills: [...current.skills, { name: "", sub: "", level: 0.8 }] }))} className="pa-primary">
          Add skill
        </button>
      </div>
      <div className="pa-skills-grid">
        {skills.map((skill, index) => {
          const percent = Math.round(Number(skill.level || 0) * 100);
          return (
            <div key={index} className="pa-card pa-skill-card">
              <div className="pa-skill-head">
                <div className="pa-skill-title">
                  <strong>{skill.name || `Skill ${index + 1}`}</strong>
                  <span>{skill.sub || "No subtitle yet"}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setDraft((current) => ({ ...current, skills: current.skills.filter((_, itemIndex) => itemIndex !== index) }))}
                  className="pa-icon-button"
                  aria-label={`Remove ${skill.name || `skill ${index + 1}`}`}
                  title="Remove skill"
                >
                  x
                </button>
              </div>
              <div className="pa-skill-fields">
                <Field label="Skill name"><Input value={skill.name} onChange={(value) => update(index, "name", value)} placeholder="iOS / Swift" /></Field>
                <Field label="Subtitle"><Input value={skill.sub} onChange={(value) => update(index, "sub", value)} placeholder="SwiftUI, UIKit, Combine" /></Field>
              </div>
              <div className="pa-skill-slider">
                <label className="pa-label" style={{ marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                  <span>Proficiency slider</span><span style={{ color: "var(--text-main)", fontWeight: 600 }}>{percent}%</span>
                </label>
                <input
                  className="pa-range"
                  type="range"
                  min={10}
                  max={100}
                  value={percent}
                  onChange={(event) => update(index, "level", Number(event.target.value) / 100)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProjectsEditor({ projects, setDraft }) {
  const [expanded, setExpanded] = useState(null);
  const update = (index, key, value) => {
    setDraft((current) => ({
      ...current,
      projects: current.projects.map((project, itemIndex) => (itemIndex === index ? { ...project, [key]: value } : project)),
    }));
  };

  return (
    <div className="pa-animate">
      <SectionHeader title="Projects" sub="Add, edit or remove portfolio projects" />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
        <button
          type="button"
          onClick={() => setDraft((current) => {
            const nextIndex = current.projects.length;
            setExpanded(nextIndex);
            return { ...current, projects: [...current.projects, { tag: "", title: "", desc: "", stack: [], link: "#" }] };
          })}
          className="pa-primary"
        >
          Add project
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {projects.map((project, index) => (
          <div key={index} className="pa-card pa-record-card">
            <button
              type="button"
              onClick={() => setExpanded(expanded === index ? null : index)}
              className={`pa-record-summary ${expanded === index ? "active" : ""}`}
            >
              <span className="pa-record-chip">{project.tag || "No tag"}</span>
              <span className="pa-record-main">
                <strong>{project.title || "Untitled project"}</strong>
                <span>{project.stack?.length ? stackToText(project.stack) : "No stack added"}</span>
              </span>
              <span className="pa-record-action">{expanded === index ? "Close" : "Edit"}</span>
            </button>
            {expanded === index && (
              <div className="pa-record-body">
                <div className="pa-record-body-head">
                  <strong>{project.title || `Project ${index + 1}`}</strong>
                  <button
                    type="button"
                    onClick={() => setDraft((current) => ({ ...current, projects: current.projects.filter((_, itemIndex) => itemIndex !== index) }))}
                    className="pa-icon-button"
                    aria-label={`Remove ${project.title || `project ${index + 1}`}`}
                    title="Remove project"
                  >
                    x
                  </button>
                </div>
                <div className="pa-grid-2">
                  <Field label="Title"><Input value={project.title} onChange={(value) => update(index, "title", value)} placeholder="App Name - Category" /></Field>
                  <Field label="Tag"><Input value={project.tag} onChange={(value) => update(index, "tag", value)} placeholder="iOS - Swift" /></Field>
                </div>
                <Field label="Description"><Textarea value={project.desc} onChange={(value) => update(index, "desc", value)} placeholder="Describe the project..." rows={3} /></Field>
                <div className="pa-grid-2">
                  <Field label="Tech Stack (comma separated)"><Input value={stackToText(project.stack)} onChange={(value) => update(index, "stack", textToStack(value))} placeholder="Swift, SwiftUI, Core Data" /></Field>
                  <Field label="Link"><Input value={project.link} onChange={(value) => update(index, "link", value)} placeholder="https://..." /></Field>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExperienceEditor({ experience, setDraft }) {
  const [expanded, setExpanded] = useState(null);
  const update = (index, key, value) => {
    setDraft((current) => ({
      ...current,
      experience: current.experience.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)),
    }));
  };

  return (
    <div className="pa-animate">
      <SectionHeader title="Experience" sub="Manage your work history and achievements" />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
        <button
          type="button"
          onClick={() => setDraft((current) => {
            const nextIndex = current.experience.length;
            setExpanded(nextIndex);
            return { ...current, experience: [...current.experience, { period: "", company: "", role: "", desc: "", achievements: [] }] };
          })}
          className="pa-primary"
        >
          Add experience
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {experience.map((item, index) => (
          <div key={index} className="pa-card pa-record-card">
            <button
              type="button"
              onClick={() => setExpanded(expanded === index ? null : index)}
              className={`pa-record-summary ${expanded === index ? "active" : ""}`}
            >
              <span className="pa-record-chip">{item.period || "No period"}</span>
              <span className="pa-record-main">
                <strong>{item.role || "Untitled role"}</strong>
                <span>{item.company || "No company"}</span>
              </span>
              <span className="pa-record-action">{expanded === index ? "Close" : "Edit"}</span>
            </button>
            {expanded === index && (
              <div className="pa-record-body">
                <div className="pa-record-body-head">
                  <strong>{item.company || `Experience ${index + 1}`}</strong>
                  <button
                    type="button"
                    onClick={() => setDraft((current) => ({ ...current, experience: current.experience.filter((_, itemIndex) => itemIndex !== index) }))}
                    className="pa-icon-button"
                    aria-label={`Remove ${item.company || `experience ${index + 1}`}`}
                    title="Remove experience"
                  >
                    x
                  </button>
                </div>
                <div className="pa-grid-3">
                  <Field label="Period"><Input value={item.period} onChange={(value) => update(index, "period", value)} placeholder="2022 - Present" /></Field>
                  <Field label="Company"><Input value={item.company} onChange={(value) => update(index, "company", value)} placeholder="Company Name" /></Field>
                  <Field label="Role"><Input value={item.role} onChange={(value) => update(index, "role", value)} placeholder="Senior Engineer" /></Field>
                </div>
                <Field label="Description"><Textarea value={item.desc} onChange={(value) => update(index, "desc", value)} rows={2} placeholder="Brief role description..." /></Field>
                <Field label="Achievements (one per line)"><Textarea value={arrayToLines(item.achievements)} onChange={(value) => update(index, "achievements", linesToArray(value))} rows={4} placeholder={"Reduced app startup time by 60%\nArchitected shared component library"} /></Field>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResumeEditor({ resumeUrl, token, onUploaded, onAuthExpired }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const resumeHref = getAssetUrl(resumeUrl);

  const handleUpload = async () => {
    if (!file) {
      setStatus({ type: "danger", message: "Choose a resume file first" });
      return;
    }

    setUploading(true);
    setStatus(null);

    try {
      const saved = await uploadResume(file, token);
      onUploaded(saved);
      setFile(null);
      setStatus({ type: "success", message: "Resume uploaded" });
    } catch (error) {
      setStatus({ type: "danger", message: error.message });
      if (error.message === "Admin login required") {
        onAuthExpired();
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pa-animate">
      <SectionHeader title="Resume" sub="Upload the file used by the footer Resume / CV link" />
      <div className="pa-card">
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Field label="Current file">
            {resumeHref ? (
              <a href={resumeHref} target="_blank" rel="noreferrer" style={{ color: "var(--text-main)", fontSize: "0.9rem", wordBreak: "break-all" }}>
                {resumeUrl}
              </a>
            ) : (
              <div style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>No resume uploaded</div>
            )}
          </Field>

          <Field label="Upload resume">
            <input
              className="pa-input"
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Field>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <div className={`pa-status ${status?.type || ""}`}>
              {status?.message || (file ? file.name : "PDF, DOC, or DOCX up to 5 MB")}
            </div>
            <button type="button" className="pa-primary" onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? "Uploading..." : "Upload resume"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SaveBanner({ dirty, saving, status, onSave, onReset }) {
  return (
    <div className="pa-top-actions">
      {status && <span className={`pa-status ${status.type}`}>{status.message}</span>}
      {!status && <span className="pa-status">{dirty ? "Unsaved changes" : "Saved"}</span>}
      <button type="button" className="pa-outline" onClick={onReset} disabled={!dirty || saving}>Reset</button>
      <button type="button" className="pa-primary" onClick={onSave} disabled={!dirty || saving}>
        {saving ? "Saving..." : "Save changes"}
      </button>
    </div>
  );
}

export default function AdminDashboard({ data, loading, apiError, onSaved }) {
  const [draft, setDraft] = useState(() => cloneData(data));
  const [active, setActive] = useState("overview");
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [session, setSession] = useState(() => {
    const saved = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!saved) return null;

    try {
      return JSON.parse(saved);
    } catch (error) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  });

  useEffect(() => {
    setDraft(cloneData(data));
  }, [data]);

  const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(data), [draft, data]);

  const save = async () => {
    if (!isDirty) return;
    setSaving(true);
    setStatus(null);

    try {
      const saved = await savePortfolioData(draft, session?.token);
      onSaved(saved);
      setStatus({ type: "success", message: "Changes saved" });
    } catch (error) {
      setStatus({ type: "danger", message: error.message });
      if (error.message === "Admin login required") {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setSession(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setSession(null);
    setStatus(null);
  };

  const handleLogin = (nextSession) => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
    setSession(nextSession);
  };

  const renderPage = () => {
    if (active === "overview") return <Overview draft={draft} />;
    if (active === "profile") return <ProfileEditor draft={draft} setDraft={setDraft} />;
    if (active === "skills") return <SkillsEditor skills={draft.skills} setDraft={setDraft} />;
    if (active === "projects") return <ProjectsEditor projects={draft.projects} setDraft={setDraft} />;
    if (active === "experience") return <ExperienceEditor experience={draft.experience} setDraft={setDraft} />;
    if (active === "resume") {
      return (
        <ResumeEditor
          resumeUrl={draft.resumeUrl}
          token={session?.token}
          onUploaded={(saved) => {
            onSaved(saved);
            setDraft(cloneData(saved));
          }}
          onAuthExpired={() => {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            setSession(null);
          }}
        />
      );
    }
    return null;
  };

  if (!session) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="pa-shell" style={{ display: "flex", minHeight: "100vh" }}>
      <style>{adminCss}</style>
      <Sidebar active={active} setActive={setActive} onLogout={logout} name={draft.name} />
      <main className="pa-main">
        <MobileNav active={active} setActive={setActive} />
        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "flex-start", marginBottom: "1rem" }}>
          <div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginBottom: 2 }}>{API_BASE_URL}</div>
            <div style={{ fontSize: "0.82rem", color: apiError ? "#c0392b" : "var(--text-muted)" }}>
              {loading ? "Loading portfolio data" : apiError ? apiError : `Signed in as ${session.username}`}
            </div>
          </div>
        </div>
        {active !== "overview" && active !== "resume" && (
          <SaveBanner
            dirty={isDirty}
            saving={saving}
            status={status}
            onSave={save}
            onReset={() => {
              setDraft(cloneData(data));
              setStatus({ type: "success", message: "Draft reset" });
            }}
          />
        )}
        {renderPage()}
      </main>
    </div>
  );
}
